import { useMemo, useState } from "react";
import { IDatabaseLineItem } from "../../types";
import { IXactimateToolRowData } from "../../../../../shared/common/Azure";
import { buildGoodMappingGroups } from "../../../../../utils/validateXactimateMappings";
import { normalizeString } from "../../../../../utils/utils";
import { SectionHeader } from "./shared";
import { MappingCard } from "./MappingCard";
import { VariantOverrideCard } from "./VariantOverrideCard";

interface MappingsSectionProps {
  lineItem: IDatabaseLineItem;
  mappings: IXactimateToolRowData[];
  collapsed: boolean;
  onToggle: () => void;
  label?: string;
  variant?: "good" | "bad";
  onUpdateMapping?: (updated: IXactimateToolRowData) => Promise<void> | void;
  onDeleteMapping?: (id: string) => Promise<void> | void;
  onCreateMapping?: (mapping: IXactimateToolRowData) => Promise<void>;
}

export const MappingsSection = ({
  lineItem,
  mappings,
  collapsed,
  onToggle,
  label = "Valid Mappings",
  variant = "good",
  onUpdateMapping,
  onDeleteMapping,
  onCreateMapping,
}: MappingsSectionProps) => {
  const groups = useMemo(() => buildGoodMappingGroups(lineItem, mappings), [lineItem, mappings]);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) =>
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <section>
      <button className="flex items-center gap-2 w-full text-left" onClick={onToggle}>
        <SectionHeader label={label} count={mappings.length} status={variant} />
        <span className="text-xs text-stone-400 ml-1">{collapsed ? "▸ show" : "▾ hide"}</span>
      </button>

      {!collapsed && (
        <div className="space-y-2 mt-2">
          {groups.map((item) => {
            if (item.type === "grouped") {
              const key = item.representative.id;
              const isExpanded = expandedKeys.has(key);
              const hiddenCount = item.coveredPermutations.length - 1;
              const representativeNormalized = normalizeString(item.representative.acceptedLineString ?? "");
              const variantPerms = item.coveredPermutations.filter((p) => p !== representativeNormalized);
              const mappingByPerm = new Map(
                item.mappings.map((m) => [normalizeString(m.acceptedLineString ?? ""), m] as const),
              );

              return (
                <div key={key} className="space-y-2">
                  <MappingCard
                    mapping={item.representative}
                    variant={variant}
                    onUpdate={onUpdateMapping}
                    onDelete={onDeleteMapping}
                  >
                    {hiddenCount > 0 && (
                      <button
                        onClick={() => toggleExpanded(key)}
                        className="text-xs text-stone-400 hover:text-stone-600"
                      >
                        {isExpanded ? "▾ collapse" : `▸ +${hiddenCount} action variant${hiddenCount !== 1 ? "s" : ""}`}
                      </button>
                    )}
                  </MappingCard>

                  {isExpanded &&
                    variantPerms.map((perm) => {
                      const existing = mappingByPerm.get(perm);
                      if (existing) {
                        return (
                          <div key={perm} className="ml-6">
                            <MappingCard
                              mapping={existing}
                              variant={variant}
                              onUpdate={onUpdateMapping}
                              onDelete={onDeleteMapping}
                            />
                          </div>
                        );
                      }
                      if (!onCreateMapping) {
                        return (
                          <p key={perm} className="font-mono text-xs text-stone-400 ml-6">
                            {perm}
                          </p>
                        );
                      }
                      return (
                        <VariantOverrideCard
                          key={perm}
                          perm={perm}
                          inheritedFrom={item.representative}
                          onCreate={onCreateMapping}
                        />
                      );
                    })}
                </div>
              );
            }

            return (
              <MappingCard
                key={item.mapping.id}
                mapping={item.mapping}
                variant={variant}
                onUpdate={onUpdateMapping}
                onDelete={onDeleteMapping}
              />
            );
          })}
        </div>
      )}
    </section>
  );
};
